import { datadogRum } from '@datadog/browser-rum'
import * as v from 'valibot'

const ignoredErrorStackIncludes = [
  'https://js-eu1.hubspot.com',
  'chrome-extension://',
]

const rawErrorSchema = v.object({
  source: v.literal('console'),
  stack: v.pipe(
    v.string(),
    v.check((stack) => ignoredErrorStackIncludes.some((ignored) => stack.includes(ignored))),
  ),
})

const ignoredErrorSchema = v.union([
  v.object({
    attributes: rawErrorSchema,
  }),
  v.object({
    error: rawErrorSchema,
  }),
])

function nonEmptyString(fieldName: string) {
  return v.pipe(
    v.string(`dd.${fieldName} must be a string`),
    v.trim(),
    v.nonEmpty(`dd.${fieldName} cannot be empty`),
  )
}

const ddConfigSchema = v.object({
  applicationId: nonEmptyString('applicationId'),
  clientToken: nonEmptyString('clientToken'),
  version: nonEmptyString('version'),
  env: nonEmptyString('env'),
  service: nonEmptyString('service'),
  site: nonEmptyString('site'),
})

/**
 * Add integration with CookieYes to update tracking consent if analytics is accepted by the user
 *
 * @see https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
 */
function addCookieYesListener() {
  document.addEventListener('cookieyes_consent_update', (data) => {
    if (data?.detail?.accepted?.includes('analytics')) {
      datadogRum.setTrackingConsent('granted')
    } else {
      datadogRum.setTrackingConsent('not-granted')
    }
  })
}

export default defineNuxtPlugin(
  ({
    $config: {
      public: { datadog, urls },
    },
  }) => {
    const config = v.safeParse(ddConfigSchema, datadog)
    if (!config.success) {
      return
    }

    const consent = window.getCkyConsent?.()

    const hasConsented = consent?.categories?.analytics

    datadogRum.init({
      ...config.output,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      trackingConsent: (hasConsented || config.output.env === 'test') ? 'granted' : 'not-granted',
      allowedTracingUrls: [
        window.location.origin,
        ...Object.values(urls.core),
        urls.crystallize,
      ],
      beforeSend(event) {
        return !v.is(ignoredErrorSchema, event.error)
      },
    })

    addCookieYesListener()
  },
)
